import {
  ArrowRight,
  Building2,
  Calendar,
  CheckCircle,
  ChevronDown,
  ClipboardList,
  Clock,
  FileText,
  Globe,
  HeadphonesIcon,
  HelpCircle,
  Instagram,
  Mail,
  MapPin,
  MessageCircle,
  Phone,
  RefreshCw,
  Route,
  Send,
  Shield,
  Star,
  Truck,
  Users,
  X,
  Zap
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useLang } from "./context/LangContext";
import Layout from "./components/Layout";
import ServiceDetail from "./pages/ServiceDetail";
import { Helmet } from "react-helmet-async";

function App() {
  const { lang, t } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    service: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle");
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    const data = new FormData();
    data.append("fullName", formData.fullName);
    data.append("email", formData.email);
    data.append("phone", formData.phone);
    data.append("service", formData.service);
    data.append("message", formData.message);
    try {
      const res = await fetch("https://formsubmit.co/ajax/info@abualilogistics.com", {
        method: "POST",
        headers: { Accept: "application/json" },
        body: data
      });
      if (res.ok) {
        setSubmitStatus("success");
        setFormData({ fullName: "", email: "", phone: "", service: "", message: "" });
      } else {
        setSubmitStatus("error");
      }
    } catch {
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus("idle"), 5000);
    }
  };

  const handleQuoteRequest = () => {
    const element = document.getElementById("contact");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setFormData((prev) => ({ ...prev, service: "quote" }));
    }
  };

  const handleConsultationRequest = () => {
    const element = document.getElementById("contact");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setFormData((prev) => ({ ...prev, service: "other" }));
    }
  };

  const services = [
    {
      icon: <Truck className="w-8 h-8" />,
      title: "Road Transportation",
      titleAr: "النقل البري",
      description: "Reliable road transportation across Bahrain and GCC countries with secure and timely delivery",
      descriptionAr: "نقل بري موثوق عبر البحرين ودول مجلس التعاون الخليجي مع توصيل آمن وفي الوقت المحدد",
      features: ["Bahrain to GCC Routes", "Timely Delivery", "Secure Transport", "Fleet Management"],
      featuresAr: ["طرق البحرين إلى الخليج", "توصيل في الوقت المحدد", "نقل آمن", "إدارة الأسطول"],
      slug: "road-transportation"
    },
    {
      icon: <RefreshCw className="w-8 h-8" />,
      title: "Re-Export Services",
      titleAr: "خدمات إعادة التصدير",
      description: "Professional re-export solutions from Bahrain with complete documentation and customs support",
      descriptionAr: "حلول إعادة تصدير احترافية من البحرين مع دعم كامل للوثائق والجمارك",
      features: ["Re-Export Documentation", "Customs Support", "Regulatory Compliance", "Fast Processing"],
      featuresAr: ["وثائق إعادة التصدير", "دعم جمركي", "الامتثال التنظيمي", "معالجة سريعة"],
      slug: "re-export"
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Customs Clearance",
      titleAr: "التخليص الجمركي",
      description: "Fast customs clearance for imports, exports and re-export shipments across GCC borders",
      descriptionAr: "تخليص جمركي سريع للشحنات المستوردة والمصدرة والمعاد تصديرها عبر حدود دول مجلس التعاون الخليجي",
      features: ["Import Clearance", "Export Clearance", "Re-Export Clearance", "Document Preparation"],
      featuresAr: ["تخليص استيراد", "تخليص تصدير", "تخليص إعادة تصدير", "إعداد المستندات"],
      slug: "customs-clearance"
    },
    {
      icon: <Route className="w-8 h-8" />,
      title: "Cross Border Shipping",
      titleAr: "الشحن عبر الحدود",
      description: "Efficient cross-border cargo transportation between GCC countries with full compliance",
      descriptionAr: "شحن بضائع فعال عبر الحدود بين دول مجلس التعاون الخليجي مع الامتثال الكامل",
      features: ["GCC Cross Border", "Customs Documentation", "Route Planning", "Border Coordination"],
      featuresAr: ["شحن عبر حدود الخليج", "توثيق جمركي", "تخطيط الطرق", "تنسيق حدودي"],
      slug: "cross-border"
    },
    {
      icon: <FileText className="w-8 h-8" />,
      title: "Import & Export Documentation",
      titleAr: "مستندات الاستيراد والتصدير",
      description: "Preparation and management of all required shipping documentation for import and export",
      descriptionAr: "إعداد وإدارة جميع مستندات الشحن المطلوبة للاستيراد والتصدير",
      features: ["Document Preparation", "Compliance Checks", "Regulatory Filing", "Digital Records"],
      featuresAr: ["إعداد المستندات", "فحوص الامتثال", "تقديم تنظيمي", "سجلات رقمية"],
      slug: "import-export-documentation"
    }
  ];

  const benefits = [
    {
      icon: <Clock className="w-6 h-6" />,
      title: "24/7 Operations",
      titleAr: "عمليات على مدار الساعة",
      description: "Round-the-clock service availability for urgent logistics needs",
      descriptionAr: "خدمة على مدار الساعة لتلبية احتياجاتك اللوجستية العاجلة"
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Expert Team",
      titleAr: "فريق خبير",
      description: "Experienced logistics professionals with deep GCC market knowledge",
      descriptionAr: "متخصصون لوجستيون ذوو خبرة مع معرفة عميقة بسوق الخليج"
    },
    {
      icon: <CheckCircle className="w-6 h-6" />,
      title: "Reliable Service",
      titleAr: "خدمة موثوقة",
      description: "Consistent on-time delivery across all GCC routes",
      descriptionAr: "توصيل منتظم في الوقت المحدد عبر جميع طرق الخليج"
    },
    {
      icon: <HeadphonesIcon className="w-6 h-6" />,
      title: "Dedicated Support",
      titleAr: "دعم مخصص",
      description: "Personal account management for every client",
      descriptionAr: "إدارة حسابات شخصية لكل عميل"
    }
  ];

  const sectionPaths = ["/about", "/services", "/service-areas", "/faq", "/rate-us", "/contact"];
  const isServiceRoute = ["/road-transportation", "/re-export", "/customs-clearance", "/cross-border", "/import-export-documentation"].includes(location.pathname);
  const isSectionRoute = sectionPaths.includes(location.pathname);

  const sectionMeta: Record<string, { title: string; desc: string; titleAr: string; descAr: string }> = {
    "rate-us": {
      title: "Rate ABU ALI LOGISTICS | Leave a Google Review",
      desc: "Share your experience with ABU ALI LOGISTICS SERVICES. Your feedback helps us improve our GCC road transportation services.",
      titleAr: "قيّم أبو علي اللوجستية | اترك تقييماً على Google",
      descAr: "شاركنا تجربتك مع خدمات أبو علي اللوجستية. رأيك يساعدنا في تطوير خدمات النقل البري لدينا في دول الخليج."
    },
    about: {
      title: "About ABU ALI LOGISTICS | GCC Road Freight Experts",
      desc: "Learn about ABU ALI LOGISTICS SERVICES — Bahrain's trusted road transportation establishment serving the GCC since 2024.",
      titleAr: "عن أبو علي اللوجستية | خبراء الشحن البري في الخليج",
      descAr: "تعرف على خدمات أبو علي اللوجستية — مؤسسة النقل البري الموثوقة في البحرين لخدمة دول الخليج منذ 2024."
    },
    services: {
      title: "Our Services | Road Freight, Re-Export & Customs Clearance",
      desc: "Explore ABU ALI LOGISTICS services: road transportation, re-export, customs clearance, cross border shipping & documentation.",
      titleAr: "خدماتنا | النقل البري، إعادة التصدير والتخليص الجمركي",
      descAr: "اطلع على خدمات أبو علي اللوجستية: النقل البري، إعادة التصدير، التخليص الجمركي، الشحن عبر الحدود والتوثيق."
    },
    "service-areas": {
      title: "Service Areas | GCC, Yemen, Syria & Jordan Coverage",
      desc: "ABU ALI LOGISTICS covers Bahrain, Saudi Arabia, UAE, Kuwait, Qatar, Oman, Yemen, Syria & Jordan — road freight across 9 countries.",
      titleAr: "مناطق الخدمة | تغطية الخليج واليمن وسوريا والأردن",
      descAr: "أبو علي اللوجستية تغطي البحرين، السعودية، الإمارات، الكويت، قطر، عمان، اليمن، سوريا والأردن — شحن بري عبر 9 دول."
    },
    faq: {
      title: "FAQ | ABU ALI LOGISTICS Road Transportation",
      desc: "Frequently asked questions about ABU ALI LOGISTICS SERVICES — road freight, customs clearance, re-export and GCC shipping.",
      titleAr: "الأسئلة الشائعة | أبو علي اللوجستية للنقل البري",
      descAr: "أسئلة شائعة حول خدمات أبو علي اللوجستية — النقل البري، التخليص الجمركي، إعادة التصدير والشحن في الخليج."
    },
    contact: {
      title: "Contact ABU ALI LOGISTICS | Get a Free Quote",
      desc: "Contact ABU ALI LOGISTICS SERVICES for road transportation across the GCC. Request a free quote via phone, WhatsApp or our form.",
      titleAr: "اتصل بأبو علي اللوجستية | احصل على عرض سعر مجاني",
      descAr: "اتصل بخدمات أبو علي اللوجستية للنقل البري عبر دول الخليج. اطلب عرض سعر مجاني عبر الهاتف أو واتساب أو النموذج."
    }
  };

  const currentSection = isSectionRoute ? location.pathname.replace("/", "") : "";
  const meta = currentSection && sectionMeta[currentSection] ? sectionMeta[currentSection] : null;

  const pageTitle = meta
    ? (lang === "en" ? meta.title : meta.titleAr)
    : "ABU ALI LOGISTICS SERVICES | GCC Road Transportation Bahrain";
  const pageDesc = meta
    ? (lang === "en" ? meta.desc : meta.descAr)
    : "ABU ALI LOGISTICS SERVICES — Bahrain's trusted road transportation establishment. GCC road freight, re-export, customs clearance & cross border shipping.";
  const pageUrl = currentSection
    ? `https://abualilogistics.com/${currentSection}`
    : "https://abualilogistics.com/";


  const stats = [
    { number: "500+", label: "Shipments Delivered", labelAr: "شحنة تم تسليمها", desc: t("Shipments across GCC routes since 2024", "شحنة عبر طرق الخليج منذ 2024") },
    { number: "10+", label: "GCC Partners", labelAr: "شريك في دول الخليج", desc: t("Trusted businesses across 6 countries", "شركات موثوقة في 6 دول") },
    { number: "99.9%", label: "On-Time Delivery", labelAr: "التسليم في الوقت المحدد", desc: t("Consistent reliability record", "سجل موثوقية ثابت") },
    { number: "24/7", label: "Customer Support", labelAr: "دعم العملاء", desc: t("Dedicated support team always available", "فريق دعم مخصص متاح دائماً") }
  ];

  const serviceAreas = [
    { name: "Bahrain", nameAr: "البحرين", flag: "🇧🇭", descriptionAr: "المقر الرئيسي وقاعدة العمليات الأساسية", description: "Headquarters & primary operations base" },
    { name: "Saudi Arabia", nameAr: "المملكة العربية السعودية", flag: "🇸🇦", descriptionAr: "طرق منتظمة إلى الدمام والرياض وجدة", description: "Regular routes to Dammam, Riyadh & Jeddah" },
    { name: "United Arab Emirates", nameAr: "الإمارات العربية المتحدة", flag: "🇦🇪", descriptionAr: "اتصالات مباشرة إلى دبي وأبوظبي", description: "Direct connections to Dubai & Abu Dhabi" },
    { name: "Kuwait", nameAr: "الكويت", flag: "🇰🇼", descriptionAr: "خدمة موثوقة إلى الكويت", description: "Reliable service to Kuwait" },
    { name: "Qatar", nameAr: "قطر", flag: "🇶🇦", descriptionAr: "نقل عبر الحدود إلى الدوحة", description: "Cross-border transport to Doha" },
    { name: "Oman", nameAr: "عُمان", flag: "🇴🇲", descriptionAr: "تغطية الطرق إلى مسقط وصحار", description: "Route coverage to Muscat & Sohar" },
    { name: "Yemen", nameAr: "اليمن", flag: "🇾🇪", descriptionAr: "خدمة نقل بري إلى اليمن", description: "Road freight service to Yemen" },
    { name: "Syria", nameAr: "سوريا", flag: "🇸🇾", descriptionAr: "خدمة نقل بري إلى سوريا", description: "Road freight service to Syria" },
    { name: "Jordan", nameAr: "الأردن", flag: "🇯🇴", descriptionAr: "خدمة نقل بري إلى الأردن", description: "Road freight service to Jordan" }
  ];

  const steps = [
    {
      icon: <MessageCircle className="w-6 h-6" />,
      step: "01",
      title: "Request a Quote",
      titleAr: "طلب عرض سعر",
      description: "Contact us with your shipment details and requirements",
      descriptionAr: "تواصل معنا بتفاصيل شحنتك ومتطلباتك"
    },
    {
      icon: <ClipboardList className="w-6 h-6" />,
      step: "02",
      title: "Documentation",
      titleAr: "توثيق",
      description: "We handle all paperwork, customs forms and compliance documentation",
      descriptionAr: "نتولى جميع الأوراق والنماذج الجمركية ووثائق الامتثال"
    },
    {
      icon: <Truck className="w-6 h-6" />,
      step: "03",
      title: "Pickup & Transport",
      titleAr: "الاستلام والنقل",
      description: "We collect your cargo and transport it via our GCC network",
      descriptionAr: "نستلم بضائعك وننقلها عبر شبكتنا في دول الخليج"
    },
    {
      icon: <CheckCircle className="w-6 h-6" />,
      step: "04",
      title: "Delivery Confirmation",
      titleAr: "تأكيد التسليم",
      description: "Real-time tracking updates and delivery confirmation",
      descriptionAr: "تحديثات تتبع فوري وتأكيد التسليم"
    }
  ];

  const testimonials = [
    {
      name: "Ahmed Al Mansouri",
      nameAr: "أحمد المنصوري",
      role: "Import Manager, Bahrain Trading Co.",
      roleAr: "مدير الاستيراد، شركة البحرين للتجارة",
      text: "Abu Ali Logistics handles our regular shipments from Bahrain to Saudi Arabia. Their customs clearance team is exceptionally efficient.",
      textAr: "تتعامل أبو علي اللوجستية مع شحناتنا المنتظمة من البحرين إلى المملكة العربية السعودية. فريق التخليص الجمركي لديهم فعال بشكل استثنائي.",
      rating: 5
    },
    {
      name: "Sarah Al Qasimi",
      nameAr: "سارة القاسمي",
      role: "Operations Director, UAE Logistics Group",
      roleAr: "مديرة العمليات، مجموعة الإمارات اللوجستية",
      text: "Reliable cross-border transportation partner. They understand GCC regulations and deliver on time consistently.",
      textAr: "شريك نقل عبر الحدود موثوق. يفهمون لوائح دول مجلس التعاون الخليجي ويقومون بالتسليم في الوقت المحدد باستمرار.",
      rating: 5
    },
    {
      name: "Khalid Al Mutairi",
      nameAr: "خالد المطيري",
      role: "Supply Chain Manager, Kuwait Enterprises",
      roleAr: "مدير سلسلة التوريد، المؤسسات الكويتية",
      text: "Their re-export documentation service saved us weeks of paperwork. Professional and highly recommended.",
      textAr: "خدمة توثيق إعادة التصدير الخاصة بهم وفرت لنا أسابيع من الأعمال الورقية. احترافية وموصى بها بشدة.",
      rating: 5
    }
  ];

  const faqs = [
    {
      question: "What road transportation services does Abu Ali Logistics provide?",
      questionAr: "ما هي خدمات النقل البري التي تقدمها أبو علي اللوجستية؟",
      answer: "We provide reliable road transportation services across Bahrain and all GCC countries including Saudi Arabia, UAE, Kuwait, Qatar, and Oman. Our fleet handles commercial cargo and freight of various sizes.",
      answerAr: "نحن نقدم خدمات النقل البري الموثوقة في جميع أنحاء البحرين وجميع دول مجلس التعاون الخليجي بما في ذلك المملكة العربية السعودية والإمارات والكويت وقطر وعمان. أسطولنا يتعامل مع البضائع التجارية والشحن بمختلف الأحجام."
    },
    {
      question: "How long does road freight take from Bahrain to Saudi Arabia?",
      questionAr: "كم تستغرق الشحنات البرية من البحرين إلى المملكة العربية السعودية؟",
      answer: "Road freight from Bahrain to Saudi Arabia typically takes 1-2 days depending on the destination. We ship regularly to Dammam, Riyadh, and Jeddah.",
      answerAr: "عادة ما يستغرق الشحن البري من البحرين إلى المملكة العربية السعودية 1-2 أيام حسب الوجهة. نقوم بالشحن بانتظام إلى الدمام والرياض وجدة."
    },
    {
      question: "Do you handle customs clearance for imports and exports?",
      questionAr: "هل تقومون بالتخليص الجمركي للواردات والصادرات؟",
      answer: "Yes, we provide complete customs clearance services for imports, exports, and re-exports across GCC borders. Our team prepares all documentation and manages the clearance process from start to finish.",
      answerAr: "نعم، نقدم خدمات التخليص الجمركي الكاملة للواردات والصادرات وإعادة التصدير عبر حدود دول مجلس التعاون الخليجي. يقوم فريقنا بإعداد جميع المستندات وإدارة عملية التخليص من البداية إلى النهاية."
    },
    {
      question: "What documents are required for re-export from Bahrain?",
      questionAr: "ما هي المستندات المطلوبة لإعادة التصدير من البحرين؟",
      answer: "Re-export from Bahrain requires commercial invoice, packing list, certificate of origin, bill of lading, and any specific permits. We handle the complete documentation process for you.",
      answerAr: "تتطلب إعادة التصدير من البحرين فاتورة تجارية وقائمة تعبئة وشهادة منشأ وسند شحن وأي تصاريح محددة. نحن نتولى عملية التوثيق الكاملة نيابة عنك."
    },
    {
      question: "Which countries do you ship to?",
      questionAr: "إلى أي دول تقومون بالشحن؟",
      answer: "We provide road transportation services to Saudi Arabia, UAE, Kuwait, Qatar, Oman, Yemen, Syria & Jordan from our base in Bahrain — with competitive freight rates across all routes.",
      answerAr: "نحن نقدم خدمات النقل البري إلى السعودية والإمارات والكويت وقطر وعمان واليمن وسوريا والأردن من مقرنا في البحرين — بأسعار شحن تنافسية على جميع الطرق."
    },
    {
      question: "How can I get a quote for my shipment?",
      questionAr: "كيف يمكنني الحصول على عرض سعر لشحنتي؟",
      answer: "You can request a free quote by filling out our contact form, calling us at +973 3444 9381, or sending us a message on WhatsApp. We typically respond within 1 hour during business hours.",
      answerAr: "يمكنك طلب عرض سعر مجاني عن طريق ملء نموذج الاتصال الخاص بنا، أو الاتصال بنا على \u200E+973 3444 9381\u200E، أو إرسال رسالة لنا على واتساب. عادة ما نرد خلال ساعة واحدة خلال ساعات العمل."
    }
  ];

  if (isServiceRoute) {
    return (
      <Layout>
        <ServiceDetail />
      </Layout>
    );
  }

  return (
    <Layout>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <link rel="canonical" href={pageUrl} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDesc} />
      </Helmet>
      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/906494/pexels-photo-906494.jpeg?auto=compress&cs=tinysrgb&w=1920"
            alt={t("Logistics and shipping operations", "عمليات الشحن واللوجستيات")}
            className="w-full h-full object-cover"
            loading="eager"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-blue-900/90 via-blue-800/85 to-gray-900/80"></div>
        </div>

        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-orange-500/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-5xl mx-auto">
            <div className="h-16"></div>

            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              Abu Ali <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-orange-300">Logistics</span>
            </h1>

            <h2 className="text-xl sm:text-2xl md:text-3xl text-white/90 mb-4 font-light">خدمات ابوعلي اللوجستية</h2>

            <p className="text-lg sm:text-xl md:text-2xl text-white/90 mb-3 font-semibold">
              {t("Road Transportation &amp; Logistics Services Across the GCC", "النقل البري والخدمات اللوجستية في جميع أنحاء دول مجلس التعاون الخليجي")}
            </p>

            <p className="text-base sm:text-lg md:text-xl text-white/80 mb-10 leading-relaxed max-w-4xl mx-auto">
              {t(
                "Your trusted partner for road transportation, re-export services, and customs clearance across Bahrain, Saudi Arabia, UAE, Kuwait, Qatar, Oman, Yemen, Syria & Jordan — competitive rates guaranteed",
                "شريكك الموثوق للنقل البري وخدمات إعادة التصدير والتخليص الجمركي في جميع أنحاء البحرين والسعودية والإمارات والكويت وقطر وعمان واليمن وسوريا والأردن — بأفضل الأسعار التنافسية"
              )}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-0">
              <button
                onClick={handleQuoteRequest}
                className="group bg-gradient-to-r from-orange-500 to-orange-600 text-white px-10 py-5 rounded-xl text-lg font-semibold hover:shadow-2xl transition-all duration-300 transform hover:scale-105 flex items-center shadow-xl"
              >
                {t("Request a Free Quote", "اطلب عرض سعر مجاني")}
                <ArrowRight className={`${lang === "ar" ? "mr-2 rotate-180" : "ml-2"} w-5 h-5 group-hover:translate-x-1 transition-transform`} />
              </button>
              <a
                href="https://wa.me/97334449381"
                target="_blank"
                rel="noopener noreferrer"
                className="border-2 border-green-400/50 text-green-300 px-8 py-5 rounded-xl text-lg font-semibold hover:bg-green-500/10 transition-all duration-300 backdrop-blur-sm flex items-center justify-center"
              >
                {t("WhatsApp Us", "تواصل واتساب")}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <div className={`flex items-center mb-6 ${lang === "ar" ? "flex-row-reverse" : ""}`}>
                <div className="w-12 h-12 bg-gradient-to-br from-blue-700 to-blue-800 rounded-xl flex items-center justify-center mr-4 rtl:ml-4 rtl:mr-0 flex-shrink-0">
                  <Building2 className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                    {t("About", "عن")} <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-700 to-orange-500">{t("Abu Ali Logistics", "أبو علي اللوجستية")}</span>
                  </h2>
                  <p className="text-gray-600 text-sm mt-1">{t("Abu Ali Logistics Services", "خدمات ابوعلي اللوجستية")}</p>
                </div>
              </div>

              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                {t(
                  "ABU ALI LOGISTICS SERVICES is a Bahrain-based individual establishment specializing in road transportation and logistics across the GCC.",
                  "مؤسسة أبو علي اللوجستية هي مؤسسة فردية بحرينية متخصصة في النقل البري والخدمات اللوجستية عبر دول مجلس التعاون الخليجي."
                )}
              </p>

              <ul className="space-y-3 mb-8">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 mr-3 rtl:ml-3 rtl:mr-0 flex-shrink-0" />
                  <span className="text-gray-700">
                    {t("Registered as an Individual Establishment in Bahrain — CR: 175553-1, valid through 2027", "مسجلة كمؤسسة فردية في البحرين — السجل التجاري: 175553-1، سارية حتى 2027")}
                  </span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 mr-3 rtl:ml-3 rtl:mr-0 flex-shrink-0" />
                  <span className="text-gray-700">
                    {t("Road freight coverage: Saudi Arabia, UAE, Kuwait, Qatar, Oman, Yemen, Syria & Jordan", "تغطية الشحن البري: السعودية، الإمارات، الكويت، قطر، عمان، اليمن، سوريا والأردن")}
                  </span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 mr-3 rtl:ml-3 rtl:mr-0 flex-shrink-0" />
                  <span className="text-gray-700">
                    {t("Fully compliant with GCC customs regulations", "متوافق تماماً مع لوائح الجمارك الخليجية")}
                  </span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 mr-3 rtl:ml-3 rtl:mr-0 flex-shrink-0" />
                  <span className="text-gray-700">
                    {t("Re-export, customs clearance, and import/export documentation services", "خدمات إعادة التصدير والتخليص الجمركي ومستندات الاستيراد والتصدير")}
                  </span>
                </li>
              </ul>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {stats.map((stat, index) => (
                  <div key={index} className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm text-center">
                    <div className="text-2xl md:text-3xl font-bold text-blue-700 mb-1">{stat.number}</div>
                    <div className="text-gray-800 text-sm font-medium">{lang === "en" ? stat.label : stat.labelAr}</div>
                    <div className="text-gray-500 text-xs mt-0.5">{stat.desc}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative lg:sticky lg:top-24">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-700/20 to-orange-500/20 rounded-3xl blur-3xl"></div>
              <div className="relative bg-white rounded-3xl shadow-2xl p-8 border border-gray-100">
                <div className="flex items-center justify-center mb-6">
                  <div className="w-16 h-16 rounded-2xl overflow-hidden shadow-lg">
                    <img
                      src="/logo.png"
                      alt={t("Abu Ali Logistics Services Logo", "شعار أبو علي اللوجستية")}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">{t("Establishment Information", "معلومات المؤسسة")}</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between py-3 border-b border-gray-100">
                    <span className="text-gray-600 font-medium">{t("Commercial Registration", "السجل التجاري")}</span>
                    <span className="text-gray-900 font-semibold">175553-1</span>
                  </div>
                  <div className="flex items-center justify-between py-3 border-b border-gray-100">
                    <span className="text-gray-600 font-medium">{t("Registration Type", "نوع التسجيل")}</span>
                    <span className="text-gray-900 font-semibold">{t("Individual Establishment", "مؤسسة فردية")}</span>
                  </div>
                  <div className="flex items-center justify-between py-3 border-b border-gray-100">
                    <span className="text-gray-600 font-medium">{t("Registration Date", "تاريخ التسجيل")}</span>
                    <span className="text-gray-900 font-semibold">11/06/2024</span>
                  </div>
                  <div className="flex items-center justify-between py-3 border-b border-gray-100">
                    <span className="text-gray-600 font-medium">{t("Valid Until", "صالح حتى")}</span>
                    <span className="text-gray-900 font-semibold">11/06/2027</span>
                  </div>
                  <div className="flex items-center justify-between py-3 border-b border-gray-100">
                    <span className="text-gray-600 font-medium">{t("Status", "الحالة")}</span>
                    <span className="text-green-600 font-semibold flex items-center">
                      <CheckCircle className="w-4 h-4 mr-1" />
                      {t("Active (نشطة)", "Active (نشطة)")}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-3 border-b border-gray-100">
                    <span className="text-gray-600 font-medium">{t("Location", "الموقع")}</span>
                    <span className="text-gray-900 font-semibold text-sm">A'ALI/عالي, {t("Bahrain", "البحرين")}</span>
                  </div>
                  <div className="flex items-center justify-between py-3">
                    <span className="text-gray-600 font-medium">{t("Owner", "المالك")}</span>
                    <span className="text-gray-900 font-semibold text-sm">Abdullah A.Jabbar Abdullah</span>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-gray-100">
                  <div className={`flex items-center justify-center ${lang === "ar" ? "space-x-reverse" : "space-x-4"}`}>
                    <div className="flex items-center text-sm text-gray-600">
                      <Star className="w-4 h-4 text-yellow-500 mr-1" />
                      {t("Licensed Business", "نشاط تجاري مرخص")}
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <Shield className="w-4 h-4 text-green-500 mr-1" />
                      {t("Fully Compliant", "متوافق تماماً")}
                    </div>
                  </div>
                </div>

                <div className="mt-6">
                  <button
                    onClick={handleQuoteRequest}
                    className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transition-all duration-300 transform hover:scale-[1.02] flex items-center justify-center"
                  >
                    {t("Request a Free Quote", "اطلب عرض سعر مجاني")}
                    <ArrowRight className={`${lang === "ar" ? "mr-2 rotate-180" : "ml-2"} w-4 h-4`} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className={`flex items-center justify-center mb-6 ${lang === "ar" ? "flex-row-reverse" : ""}`}>
              <div className="w-12 h-12 bg-gradient-to-br from-blue-700 to-blue-800 rounded-xl flex items-center justify-center mr-4 rtl:ml-4 rtl:mr-0 flex-shrink-0">
                <Truck className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                {t("Our", "خدماتنا")} <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-700 to-orange-500">{t("Services", "الخدمات")}</span>
              </h2>
            </div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              {t(
                "Specialized road transportation and logistics services across Bahrain and the GCC",
                "خدمات النقل البري والخدمات اللوجستية المتخصصة في جميع أنحاء البحرين ودول مجلس التعاون الخليجي"
              )}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="group relative">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-700/5 to-orange-500/5 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-300"></div>
                <div className="relative bg-white border border-gray-100 rounded-2xl p-8 hover:shadow-2xl transition-all duration-300 group-hover:-translate-y-2 h-full flex flex-col">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-700 to-blue-800 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <div className="text-white">{service.icon}</div>
                  </div>

                  <h3 className="text-xl font-bold text-gray-900 mb-4">{lang === "en" ? service.title : service.titleAr}</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed flex-grow">{lang === "en" ? service.description : service.descriptionAr}</p>

                  <div className="space-y-2 mb-8">
                    {service.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center text-sm text-gray-600">
                        <CheckCircle className="w-4 h-4 text-green-500 mr-2 rtl:ml-2 rtl:mr-0 flex-shrink-0" />
                        {lang === "en" ? feature : service.featuresAr[featureIndex]}
                      </div>
                    ))}
                  </div>

                  <div className="mt-auto space-y-2">
                    <button
                      onClick={() => navigate(`/${service.slug}`)}
                      className="w-full bg-gradient-to-r from-blue-700 to-blue-800 text-white px-5 py-3 rounded-xl font-semibold hover:shadow-lg transition-all duration-300 transform hover:scale-[1.02] flex items-center justify-center"
                    >
                      {t("Learn More", "تعرف على المزيد")}
                      <ArrowRight className={`${lang === "ar" ? "mr-2 rotate-180" : "ml-2"} w-4 h-4`} />
                    </button>
                    <button
                      onClick={handleQuoteRequest}
                      className="w-full border-2 border-blue-700/20 text-blue-700 px-5 py-2 rounded-xl font-medium hover:bg-blue-50 transition-all duration-300 flex items-center justify-center"
                    >
                      {t("Request Quote", "طلب عرض سعر")}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className={`flex items-center justify-center mb-6 ${lang === "ar" ? "flex-row-reverse" : ""}`}>
              <div className="w-12 h-12 bg-gradient-to-br from-blue-700 to-blue-800 rounded-xl flex items-center justify-center mr-4 rtl:ml-4 rtl:mr-0 flex-shrink-0">
                <Star className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                {t("Why Choose", "لماذا تختار")} <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-700 to-orange-500">{t("Us", "نا")}</span>
              </h2>
            </div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              {t(
                "What makes Abu Ali Logistics the preferred choice for road transportation across the GCC",
                "ما الذي يجعل أبو علي اللوجستية الخيار المفضل للنقل البري في جميع أنحاء دول مجلس التعاون الخليجي"
              )}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((item, index) => (
              <div key={index} className="text-center group">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-700 to-blue-800 rounded-2xl flex items-center justify-center mx-auto mb-5 group-hover:scale-110 transition-all duration-300 shadow-lg">
                  <div className="text-white">{item.icon}</div>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{lang === "en" ? item.title : item.titleAr}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{lang === "en" ? item.description : item.descriptionAr}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Working Process Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className={`flex items-center justify-center mb-6 ${lang === "ar" ? "flex-row-reverse" : ""}`}>
              <div className="w-12 h-12 bg-gradient-to-br from-blue-700 to-blue-800 rounded-xl flex items-center justify-center mr-4 rtl:ml-4 rtl:mr-0 flex-shrink-0">
                <ClipboardList className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                {t("How It", "كيف")} <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-700 to-orange-500">{t("Works", "نعمل")}</span>
              </h2>
            </div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              {t(
                "Simple process to get your cargo moving across the GCC",
                "عملية بسيطة لنقل بضائعك عبر دول مجلس التعاون الخليجي"
              )}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="relative group">
                <div className="absolute -top-4 -right-4 rtl:right-auto rtl:-left-4 w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg z-10">
                  <span className="text-white font-bold text-sm">{step.step}</span>
                </div>
                <div className="bg-gray-50 rounded-2xl p-8 border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300 h-full">
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-700 to-blue-800 rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300">
                    <div className="text-white">{step.icon}</div>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{lang === "en" ? step.title : step.titleAr}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{lang === "en" ? step.description : step.descriptionAr}</p>
                </div>
                {index < steps.length - 1 && (
                  <div className={`hidden lg:block absolute top-1/2 ${lang === "ar" ? "-left-4 rotate-180" : "-right-4"} text-blue-700`}>
                    <ArrowRight className="w-5 h-5" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className={`flex items-center justify-center mb-6 ${lang === "ar" ? "flex-row-reverse" : ""}`}>
              <div className="w-12 h-12 bg-gradient-to-br from-blue-700 to-blue-800 rounded-xl flex items-center justify-center mr-4 rtl:ml-4 rtl:mr-0 flex-shrink-0">
                <MessageCircle className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                {t("What Our", "ماذا يقول")} <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-700 to-orange-500">{t("Clients Say", "عملاؤنا")}</span>
              </h2>
            </div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              {t(
                "Trusted by businesses across the GCC for reliable logistics services",
                "موثوق من قبل الشركات في جميع أنحاء دول مجلس التعاون الخليجي للخدمات اللوجستية الموثوقة"
              )}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((item, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300 relative">
                <div className={`absolute top-6 ${lang === "ar" ? "left-6 scale-x-[-1]" : "right-6"} text-orange-200`}>
                  <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                  </svg>
                </div>
                <p className="text-gray-600 leading-relaxed mb-6 italic">&ldquo;{lang === "en" ? item.text : item.textAr}&rdquo;</p>
                <div className={`flex items-center ${lang === "ar" ? "flex-row-reverse" : ""}`}>
                  <div className={`w-12 h-12 bg-gradient-to-br from-blue-700 to-blue-800 rounded-full flex items-center justify-center ${lang === "ar" ? "ml-4" : "mr-4"} flex-shrink-0`}>
                    <span className="text-white font-bold text-lg">{item.name.charAt(0)}</span>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">{lang === "en" ? item.name : item.nameAr}</p>
                    <p className="text-gray-500 text-xs">{lang === "en" ? item.role : item.roleAr}</p>
                    <div className={`flex items-center mt-1 ${lang === "ar" ? "flex-row-reverse" : ""}`}>
                      {[...Array(item.rating)].map((_, i) => (
                        <Star key={i} className="w-3.5 h-3.5 text-yellow-500 fill-current" />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Areas Section */}
      <section id="service-areas" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className={`flex items-center justify-center mb-6 ${lang === "ar" ? "flex-row-reverse" : ""}`}>
              <div className="w-12 h-12 bg-gradient-to-br from-blue-700 to-blue-800 rounded-xl flex items-center justify-center mr-4 rtl:ml-4 rtl:mr-0 flex-shrink-0">
                <Globe className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                {t("Service", "مناطق")} <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-700 to-orange-500">{t("Areas", "الخدمة")}</span>
              </h2>
            </div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              {t(
                "We provide road transportation and logistics services across the GCC, Yemen, Syria & Jordan with competitive rates",
                "نحن نقدم خدمات النقل البري والخدمات اللوجستية في جميع أنحاء دول الخليج واليمن وسوريا والأردن بأسعار تنافسية"
              )}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {serviceAreas.map((area, index) => (
              <div key={index} className="bg-gray-50 rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300 group">
                <div className={`flex items-center mb-4 ${lang === "ar" ? "flex-row-reverse" : ""}`}>
                  <span className={`text-4xl ${lang === "ar" ? "ml-4" : "mr-4"}`}>{area.flag}</span>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 group-hover:text-blue-700 transition-colors">{lang === "en" ? area.name : area.nameAr}</h3>
                    <p className="text-sm text-gray-500">{lang === "en" ? area.nameAr : area.name}</p>
                  </div>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">{lang === "en" ? area.description : area.descriptionAr}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <div className="inline-flex flex-wrap items-center justify-center gap-3 text-sm text-gray-500">
              <span className="px-4 py-2 bg-gray-50 rounded-full border border-gray-200 shadow-sm">🇧🇭 {t("Bahrain", "البحرين")}</span>
              <span className="px-4 py-2 bg-gray-50 rounded-full border border-gray-200 shadow-sm">🇸🇦 {t("Saudi Arabia", "السعودية")}</span>
              <span className="px-4 py-2 bg-gray-50 rounded-full border border-gray-200 shadow-sm">🇦🇪 {t("UAE", "الإمارات")}</span>
              <span className="px-4 py-2 bg-gray-50 rounded-full border border-gray-200 shadow-sm">🇰🇼 {t("Kuwait", "الكويت")}</span>
              <span className="px-4 py-2 bg-gray-50 rounded-full border border-gray-200 shadow-sm">🇶🇦 {t("Qatar", "قطر")}</span>
              <span className="px-4 py-2 bg-gray-50 rounded-full border border-gray-200 shadow-sm">🇴🇲 {t("Oman", "عمان")}</span>
              <span className="px-4 py-2 bg-gray-50 rounded-full border border-gray-200 shadow-sm">🇾🇪 {t("Yemen", "اليمن")}</span>
              <span className="px-4 py-2 bg-gray-50 rounded-full border border-gray-200 shadow-sm">🇸🇾 {t("Syria", "سوريا")}</span>
              <span className="px-4 py-2 bg-gray-50 rounded-full border border-gray-200 shadow-sm">🇯🇴 {t("Jordan", "الأردن")}</span>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className={`flex items-center justify-center mb-6 ${lang === "ar" ? "flex-row-reverse" : ""}`}>
              <div className="w-12 h-12 bg-gradient-to-br from-blue-700 to-blue-800 rounded-xl flex items-center justify-center mr-4 rtl:ml-4 rtl:mr-0 flex-shrink-0">
                <HelpCircle className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                {t("Frequently Asked", "الأسئلة")} <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-700 to-orange-500">{t("Questions", "الشائعة")}</span>
              </h2>
            </div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              {t(
                "Common questions about our road transportation and logistics services",
                "أسئلة شائعة حول خدمات النقل البري والخدمات اللوجستية لدينا"
              )}
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-8 py-5 flex items-center justify-between text-left rtl:text-right hover:bg-gray-50 transition-colors"
                  aria-expanded={openFaq === index}
                >
                  <span className="font-semibold text-gray-900 pr-4 rtl:pl-4 rtl:pr-0">{lang === "en" ? faq.question : faq.questionAr}</span>
                  <ChevronDown className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform duration-300 ${openFaq === index ? "rotate-180" : ""}`} />
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${openFaq === index ? "max-h-96" : "max-h-0"}`}>
                  <div className="px-8 pb-5 text-gray-600 leading-relaxed border-t border-gray-100 pt-4">
                    {lang === "en" ? faq.answer : faq.answerAr}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Rate Us Section */}
      <section id="rate-us" className="py-20 bg-gradient-to-br from-gray-50 via-white to-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className={`flex items-center justify-center mb-6 ${lang === "ar" ? "flex-row-reverse" : ""}`}>
              <div className="w-14 h-14 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-2xl flex items-center justify-center mr-4 rtl:ml-4 rtl:mr-0 flex-shrink-0 shadow-lg shadow-yellow-200/50">
                <Star className="w-7 h-7 text-white" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                {t("Share Your", "شاركنا")} <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-orange-500">{t("Feedback", "رأيك")}</span>
              </h2>
            </div>
            <p className="text-lg text-gray-500 max-w-2xl mx-auto leading-relaxed">
              {t(
                "Your opinion matters. Leave us a review on Google and help us serve you better.",
                "رأيك يهمنا. اترك لنا تقييماً على Google وساعدنا لنخدمك بشكل أفضل."
              )}
            </p>
          </div>

          <div className="relative bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-gray-100 overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-yellow-400 via-orange-500 to-blue-700"></div>

            <div className="text-center mb-10">
              <div className="inline-flex items-center justify-center gap-1.5 mb-5">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-10 h-10 text-yellow-400 fill-yellow-400 drop-shadow-sm" />
                ))}
              </div>
              <p className="text-xl text-gray-800 font-bold leading-relaxed">
                {t(
                  "We value your opinion — share your experience and help us serve you better.",
                  "نقدّر رأيك — شاركنا تجربتك وساعدنا لنخدمك بشكل أفضل."
                )}
              </p>
            </div>

            <div className="text-center mb-8">
              <a href="https://g.page/r/CSXfY2QDO-7BEBM/review" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 px-10 py-5 bg-gradient-to-r from-blue-700 to-blue-600 text-white rounded-2xl font-bold text-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 shadow-lg shadow-blue-200/50">
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                {t("Write a Google Review", "اكتب تقييماً على Google")}
              </a>
            </div>

            <div className="text-center">
              <a href="https://wa.me/97334449381" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-gray-400 hover:text-gray-600 transition-colors text-sm">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                {t("Or message us privately on WhatsApp", "أو راسلنا خاص على واتساب")}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gradient-to-br from-gray-900 via-blue-900 to-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className={`flex items-center justify-center mb-6 ${lang === "ar" ? "flex-row-reverse" : ""}`}>
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center mr-4 rtl:ml-4 rtl:mr-0 flex-shrink-0">
                <Phone className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white">
                {t("Get In", "تواصل")} <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-orange-300">{t("Touch", "معنا")}</span>
              </h2>
            </div>
            <p className="text-xl text-white/80 max-w-3xl mx-auto leading-relaxed">
              {t(
                "Ready to move your cargo across the GCC? Request a free quote via our form, phone or WhatsApp.",
                "مستعد لنقل بضائعك عبر دول الخليج؟ اطلب عرض سعر مجاني عبر نموذج الاتصال أو الهاتف أو واتساب"
              )}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            <div className="lg:col-span-3 bg-white/10 backdrop-blur-md rounded-3xl p-8 border border-white/10">
              <h3 className="text-2xl font-bold text-white mb-6">{t("Send us a Message", "أرسل لنا رسالة")}</h3>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="fullName" className="block text-white/80 text-sm font-medium mb-2">
                      {t("Full Name *", "الاسم الكامل *")}
                    </label>
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      autoComplete="name"
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent backdrop-blur-sm transition-all duration-300"
                      placeholder={t("Enter your name", "أدخل اسمك")}
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-white/80 text-sm font-medium mb-2">
                      {t("Email Address *", "البريد الإلكتروني *")}
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      autoComplete="email"
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent backdrop-blur-sm transition-all duration-300"
                      placeholder={t("Enter your email", "أدخل بريدك الإلكتروني")}
                      required
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="phone" className="block text-white/80 text-sm font-medium mb-2">
                    {t("Phone Number", "رقم الهاتف")}
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    autoComplete="tel"
                    pattern="[\+\d\s\-\(\)]{7,20}"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent backdrop-blur-sm transition-all duration-300"
                    placeholder="+973 XXXX XXXX"
                  />
                </div>
                <div>
                  <label htmlFor="service" className="block text-white/80 text-sm font-medium mb-2">
                    {t("Service Interest", "الخدمة المطلوبة")}
                  </label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent backdrop-blur-sm transition-all duration-300"
                  >
                    <option value="" className="bg-gray-800 text-white">
                      {t("Select a service", "اختر الخدمة")}
                    </option>
                    <option value="road-transport" className="bg-gray-800 text-white">
                      {lang === "en" ? "Road Transportation" : "النقل البري"}
                    </option>
                    <option value="re-export" className="bg-gray-800 text-white">
                      {lang === "en" ? "Re-Export Services" : "خدمات إعادة التصدير"}
                    </option>
                    <option value="customs" className="bg-gray-800 text-white">
                      {lang === "en" ? "Customs Clearance" : "التخليص الجمركي"}
                    </option>
                    <option value="cross-border" className="bg-gray-800 text-white">
                      {lang === "en" ? "Cross Border Shipping" : "الشحن عبر الحدود"}
                    </option>
                    <option value="documentation" className="bg-gray-800 text-white">
                      {lang === "en" ? "Import & Export Documentation" : "مستندات الاستيراد والتصدير"}
                    </option>
                    <option value="quote" className="bg-gray-800 text-white">
                      {t("General Quote", "عرض سعر عام")}
                    </option>
                    <option value="other" className="bg-gray-800 text-white">
                      {t("Other Inquiry", "استفسار آخر")}
                    </option>
                  </select>
                </div>
                <div>
                  <label htmlFor="message" className="block text-white/80 text-sm font-medium mb-2">
                    {t("Message *", "الرسالة *")}
                  </label>
                  <textarea
                    rows={5}
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    autoComplete="off"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent backdrop-blur-sm transition-all duration-300 resize-none"
                    placeholder={t("Tell us about your logistics needs...", "أخبرنا عن احتياجاتك اللوجستية...")}
                    required
                  ></textarea>
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white px-8 py-4 rounded-xl text-lg font-semibold hover:shadow-2xl transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      {t("Sending...", "جارٍ الإرسال...")}
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      {t("Send Message", "إرسال الرسالة")}
                    </>
                  )}
                </button>

                {submitStatus === "success" && (
                  <div className="bg-green-500/20 border border-green-500/30 rounded-xl p-4 text-green-300 text-center">
                    <CheckCircle className="w-5 h-5 inline mr-2" />
                    {t("Message sent successfully! We'll get back to you soon.", "تم إرسال الرسالة بنجاح! سنعود إليك قريباً.")}
                  </div>
                )}

                {submitStatus === "error" && (
                  <div className="bg-red-500/20 border border-red-500/30 rounded-xl p-4 text-red-300 text-center">
                    <X className="w-5 h-5 inline mr-2" />
                    {t("Failed to send message. Please try again or contact us directly.", "فشل إرسال الرسالة. يرجى المحاولة مرة أخرى أو الاتصال بنا مباشرة.")}
                  </div>
                )}
              </form>
            </div>

            <div className="lg:col-span-2 space-y-8">
              <div className="bg-white/10 backdrop-blur-md rounded-3xl p-8 border border-white/10">
                <h3 className="text-2xl font-bold text-white mb-6">{t("Contact Information", "معلومات الاتصال")}</h3>
                <div className="space-y-6">
                  <div className={`flex items-start ${lang === "ar" ? "flex-row-reverse space-x-reverse" : "space-x-4"}`}>
                    <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-white mb-1">{t("Business Address", "العنوان التجاري")}</h4>
                      <p className="text-white/80 text-sm leading-relaxed">
                        {t("Flat / Shop No.: 109", "محل / شقة رقم: 109")}
                        <br />
                        {t("Building: 3224", "مبنى: 3224")}
                        <br />
                        {t("Road / Street Number: 4282", "شارع / طريق رقم: 4282")}
                        <br />
                        {t("Block: 742", "قطعة: 742")}
                        <br />
                        {t("Town: A'ALI/عالي", "مدينة: عالي / A'ALI")}
                      </p>
                    </div>
                  </div>

                  <div className={`flex items-start ${lang === "ar" ? "flex-row-reverse space-x-reverse" : "space-x-4"}`}>
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-700 to-blue-800 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-white mb-1">{t("Phone Number", "رقم الهاتف")}</h4>
                      <a href="tel:+97334449381" className="text-white/80 text-sm leading-relaxed hover:text-orange-300 transition-colors">
                        {lang === "en" ? "+973 3444 9381" : <span dir="ltr">+973 3444 9381</span>}
                      </a>
                    </div>
                  </div>

                  <div className={`flex items-start ${lang === "ar" ? "flex-row-reverse space-x-reverse" : "space-x-4"}`}>
                    <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center flex-shrink-0">
                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-white mb-1">{t("WhatsApp", "واتساب")}</h4>
                      <a href="https://wa.me/97334449381" target="_blank" rel="noopener noreferrer" className="text-white/80 text-sm leading-relaxed hover:text-orange-300 transition-colors" aria-label={t("WhatsApp: +973 3444 9381", "واتساب: 973-3444-9381+")}>
                        {t("+973 3444 9381", "\u200E+973 3444 9381\u200E")}
                      </a>
                    </div>
                  </div>

                  <div className={`flex items-start ${lang === "ar" ? "flex-row-reverse space-x-reverse" : "space-x-4"}`}>
                    <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-blue-700 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-white mb-1">{t("Email Address", "البريد الإلكتروني")}</h4>
                      <a href="mailto:info@abualilogistics.com" className="text-white/80 text-sm leading-relaxed hover:text-orange-300 transition-colors">
                        info@abualilogistics.com
                      </a>
                    </div>
                  </div>

                  <div className={`flex items-start ${lang === "ar" ? "flex-row-reverse space-x-reverse" : "space-x-4"}`}>
                    <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-purple-600 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Instagram className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-white mb-1">{t("Instagram", "انستغرام")}</h4>
                      <a href="https://www.instagram.com/abuali_logistics/" target="_blank" rel="noopener noreferrer" className="text-white/80 text-sm leading-relaxed hover:text-orange-300 transition-colors">
                        {t("@abuali_logistics", "\u200E@abuali_logistics\u200E")}
                      </a>
                    </div>
                  </div>

                  <div className={`flex items-start ${lang === "ar" ? "flex-row-reverse space-x-reverse" : "space-x-4"}`}>
                    <div className="w-12 h-12 bg-black rounded-xl flex items-center justify-center flex-shrink-0">
                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-white mb-1">{t("TikTok", "تيك توك")}</h4>
                      <a href="https://www.tiktok.com/@abuali_90a" target="_blank" rel="noopener noreferrer" className="text-white/80 text-sm leading-relaxed hover:text-orange-300 transition-colors">
                        {t("@abuali_90a", "\u200E@abuali_90a\u200E")}
                      </a>
                    </div>
                  </div>

                  <div className={`flex items-start ${lang === "ar" ? "flex-row-reverse space-x-reverse" : "space-x-4"}`}>
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-700 to-orange-500 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Clock className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-white mb-1">{t("Operating Hours", "ساعات العمل")}</h4>
                      <p className="text-white/80 text-sm leading-relaxed">
                        {t("Sunday - Thursday: 8:00 AM - 6:00 PM", "\u200Eالأحد - الخميس: 8:00 صباحاً - 6:00 مساءً\u200E")}
                        <br />
                        {t("Friday - Saturday: 9:00 AM - 4:00 PM", "\u200Eالجمعة - السبت: 9:00 صباحاً - 4:00 مساءً\u200E")}
                        <br />
                        {t("24/7 Emergency Support Available", "\u200Eدعم الطوارئ متاح 24/7\u200E")}
                      </p>
                    </div>
                  </div>

                  <div className={`flex items-start ${lang === "ar" ? "flex-row-reverse space-x-reverse" : "space-x-4"}`}>
                    <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Globe className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-white mb-1">{t("Service Areas", "مناطق الخدمة")}</h4>
                      <p className="text-white/80 text-sm leading-relaxed">
                        {t("Bahrain • Saudi Arabia • UAE • Kuwait • Qatar • Oman • Yemen • Syria • Jordan", "البحرين • السعودية • الإمارات • الكويت • قطر • عمان • اليمن • سوريا • الأردن")}
                        <br />
                        <span className="text-white/60">{t("GCC, Yemen, Syria & Jordan Road Transportation Network", "شبكة النقل البري لدول الخليج واليمن وسوريا والأردن")}</span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-orange-500/20 to-blue-700/20 backdrop-blur-md rounded-3xl p-8 border border-white/10">
                <div className={`flex items-center mb-4 ${lang === "ar" ? "flex-row-reverse" : ""}`}>
                  <Zap className="w-6 h-6 text-orange-400 mr-2 rtl:ml-2 rtl:mr-0" />
                  <h3 className="text-xl font-bold text-white">{t("Ready to Get Started?", "مستعد للبدء؟")}</h3>
                </div>
                <p className="text-white/80 mb-6 leading-relaxed">
                  {t(
                    "Contact us today for a free quote and discover how our GCC road transportation and logistics services can support your business.",
                    "اتصل بنا اليوم للحصول على عرض سعر مجاني واكتشف كيف يمكن لخدمات النقل البري والخدمات اللوجستية لدينا في دول مجلس التعاون الخليجي أن تدعم أعمالك."
                  )}
                </p>
                <div className="space-y-3">
                  <button
                    onClick={handleQuoteRequest}
                    className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transition-all duration-300 transform hover:scale-105 flex items-center justify-center"
                  >
                    <MessageCircle className="w-5 h-5 mr-2" />
                    {t("Request Free Quote", "طلب عرض سعر مجاني")}
                  </button>
                  <button
                    onClick={handleConsultationRequest}
                    className="w-full border-2 border-white/30 text-white px-6 py-3 rounded-xl font-semibold hover:bg-white/10 transition-all duration-300 flex items-center justify-center"
                  >
                    <Calendar className="w-5 h-5 mr-2" />
                    {t("Schedule Consultation", "جدولة استشارة")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}

export default App;
